import type { MedplumClient } from '@medplum/core';
import type { Appointment, Encounter } from '@medplum/fhirtypes';
import type { PortalAppointment } from '../../../pages/client-portal/data/mockClientPortalData';
import {
  mockUpcomingAppointments,
  mockPastAppointments,
} from '../../../pages/client-portal/data/mockClientPortalData';
import {
  eocDisplayName,
  practitionerIdFromRef,
} from '../../../pages/client-portal/utils/portalEoc';
import { isFhirAppointmentUpcoming } from '@/lib/appointmentEffectiveEnd';
import { joinWindowClosesMs } from '@unify/fhir-model/resources/appointment';
import {
  loadPractitionerPreferredLabelMap,
  preferredLabelOrFallback,
} from '@/lib/loadPractitionerPreferredLabels';
import { countActivePatientParticipants } from '../../../pages/client-portal/utils/portalAppointmentCancel';

/** Statuses included in the near-term / upcoming calendar window search. */
const PORTAL_IN_WINDOW_APPOINTMENT_STATUSES =
  'booked,pending,proposed,arrived,checked-in,cancelled,noshow';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface AppointmentsDataConfig {
  /** When true, fetch live Appointment resources from Medplum. */
  live: boolean;
  /** Medplum client instance (required when live=true). */
  medplum?: MedplumClient;
  /**
   * When set, restricts search to this Patient (primary/dependent). Required for guarantor proxy
   * logins so results match the portal-subject patient, not the guarantor’s own Patient record.
   */
  patientId?: string;
}

export interface AppointmentsPageData {
  upcoming: PortalAppointment[];
  past: PortalAppointment[];
  source: 'mock' | 'medplum';
}

// ---------------------------------------------------------------------------
// Medplum → PortalAppointment normalizer
// ---------------------------------------------------------------------------

function mapStatus(fhirStatus: Appointment['status']): PortalAppointment['status'] {
  switch (fhirStatus) {
    case 'booked':
    case 'pending':
      return 'Scheduled';
    case 'proposed':
      return 'Requested';
    case 'fulfilled':
    case 'checked-in':
    case 'arrived':
      return 'Completed';
    case 'cancelled':
    case 'noshow':
    case 'entered-in-error':
      return 'Cancelled';
    default:
      return 'Scheduled';
  }
}

/** True when this portal patient has already cancelled / no-showed their own participant row. */
function isPortalActorAbsent(apt: Appointment, patientId: string | undefined): boolean {
  if (!patientId) return false;
  const patientRef = `Patient/${patientId}`;
  const participant = apt.participant?.find((p) => p.actor?.reference === patientRef);
  // Portal policies hide participant extensions (attendance). Manual attendance writers set `declined` with an absent
  // code; chime-auto attendance deliberately does not (the client may rejoin in the join window).
  return participant?.status === 'declined';
}

function resolveModality(
  apt: Appointment
): PortalAppointment['modality'] {
  const virtualCoding = apt.appointmentType?.coding?.find(
    (c) => c.code === 'VR' || c.display?.toLowerCase().includes('telehealth')
  );
  if (virtualCoding) return 'Telehealth';

  const virtualClass = (apt as Appointment & { class?: Encounter['class'] }).class;
  if (virtualClass?.code === 'VR') return 'Telehealth';

  return 'In person';
}

/**
 * A no-show `chime-events` derived that the client may still join (inside the join window). Portal policies
 * hide `attendance-source`, so the signal is the client's own RSVP: manual attendance writers set `declined`, while
 * chime-auto leaves it (see `isPortalActorAbsent`).
 */
function isJoinableChimeAutoNoShow(apt: Appointment, patientId: string | undefined, nowMs: number): boolean {
  if (!patientId || apt.status !== 'noshow' || isPortalActorAbsent(apt, patientId)) return false;
  return nowMs <= joinWindowClosesMs(apt, apt.start ? Date.parse(apt.start) : Number.NaN);
}

/**
 * A telehealth appointment the client may still join, so it stays Upcoming (with Join) until
 * `joinWindowClosesMs`: `booked` / `arrived` / `checked-in` or `fulfilled` of any source (matching the guest-link
 * rule), unless the client's own participant row is already cancelled or no-show (a group member who is out), or a
 * chime-auto no-show (see above).
 */
function isChimeJoinable(apt: Appointment, patientId: string | undefined, nowMs: number): boolean {
  if (resolveModality(apt) !== 'Telehealth') return false;
  if (isJoinableChimeAutoNoShow(apt, patientId, nowMs)) return true;
  const status = apt.status;
  const startMs = apt.start ? Date.parse(apt.start) : Number.NaN;
  const linkable =
    (status === 'booked' || status === 'arrived' || status === 'checked-in' || status === 'fulfilled') &&
    !isPortalActorAbsent(apt, patientId);
  return linkable && nowMs <= joinWindowClosesMs(apt, startMs);
}

function normalizeAppointment(
  apt: Appointment,
  patientId: string | undefined,
  practitionerLabels: Map<string, string>,
  nowMs: number,
): PortalAppointment {
  const practitionerParticipant = apt.participant?.find((p) =>
    p.actor?.reference?.startsWith('Practitioner/')
  );
  const providerRef = practitionerParticipant?.actor?.reference;
  const providerId = providerRef ? practitionerIdFromRef(providerRef) : undefined;
  const modality = resolveModality(apt);
  // A joinable chime-auto no-show shows Scheduled (its `noshow` status would otherwise map to Cancelled). chime-events
  // never sets the RSVP to `declined`, so a group member it marked no-show reads like any member of the `fulfilled`
  // group: a joinable fulfilled visit keeps its Completed label (it is Upcoming only so Join stays available). Only a
  // manual attendance write, which sets `declined`, shows the member Cancelled.
  const status =
    apt.status !== 'fulfilled' && isChimeJoinable(apt, patientId, nowMs)
    ? 'Scheduled'
    : isPortalActorAbsent(apt, patientId) ? 'Cancelled' : mapStatus(apt.status);

  return {
    id: apt.id ?? crypto.randomUUID(),
    start: apt.start ?? new Date().toISOString(),
    end: apt.end ?? new Date().toISOString(),
    serviceType:
      apt.serviceType?.[0]?.coding?.[0]?.display ??
      apt.serviceType?.[0]?.text ??
      'Appointment',
    providerLabel: preferredLabelOrFallback(
      providerId,
      practitionerLabels,
      practitionerParticipant?.actor?.display,
    ),
    ...(providerId ? { providerId } : {}),
    modality,
    status,
    locationLabel: modality === 'Telehealth' ? 'Telehealth' : 'Clinic',
    joinUrl: modality === 'Telehealth' ? `/client-portal/session/${apt.id}/join` : undefined,
    activePatientParticipantCount: countActivePatientParticipants(apt),
  };
}

/** Cancelled visits belong in Past even when the slot is still in the future. */
function isPortalPastAppointment(
  apt: Appointment,
  normalized: PortalAppointment,
  nowMs: number,
  patientId?: string,
): boolean {
  // The whole join window counts, not just the scheduled slot, and a joinable chime-auto no-show stays
  // upcoming (with Join) until it closes.
  if (isChimeJoinable(apt, patientId, nowMs)) return false;
  if (normalized.status === 'Cancelled') return true;
  return !isFhirAppointmentUpcoming(apt, nowMs);
}

async function enrichAppointmentsWithEpisodeOfCare(
  medplum: MedplumClient,
  patientId: string | undefined,
  appointments: PortalAppointment[]
): Promise<PortalAppointment[]> {
  if (!patientId || appointments.length === 0) return appointments;
  try {
    const eocs = await medplum.searchResources('EpisodeOfCare', {
      patient: `Patient/${patientId}`,
      _count: '100',
    });
    return appointments.map((apt) => {
      if (apt.episodeOfCareId && apt.episodeLabel) return apt;
      const pid = apt.providerId;
      if (!pid) return apt;
      const match = eocs.find(
        (e) => practitionerIdFromRef(e.careManager?.reference) === pid
      );
      if (!match?.id) return apt;
      return {
        ...apt,
        episodeOfCareId: match.id,
        episodeLabel: eocDisplayName(match),
      };
    });
  } catch {
    return appointments;
  }
}

// ---------------------------------------------------------------------------
// Data fetcher
// ---------------------------------------------------------------------------

/**
 * Fetch appointments page data.
 *
 * - In mock mode, returns the existing mock data unchanged.
 * - In live mode, queries Medplum for Appointment resources visible to the
 *   authenticated user (scoped by AccessPolicy on the server), normalizes
 *   them into PortalAppointment shape, and splits into upcoming/past.
 *
 * Server-side AccessPolicies enforce that:
 * - Patients see only their own appointments
 * - Providers see appointments for their panel
 * - Supervisors see appointments for supervised panels
 */
export async function getAppointmentsPageData(
  cfg: AppointmentsDataConfig
): Promise<AppointmentsPageData> {
  if (!cfg.live || !cfg.medplum) {
    return {
      upcoming: [...mockUpcomingAppointments],
      past: [...mockPastAppointments],
      source: 'mock',
    };
  }

  try {
    const now = new Date().toISOString();
    const nowMs = Date.now();
    const lookbackMs = 6 * 60 * 60 * 1000;
    const lookback = new Date(nowMs - lookbackMs).toISOString();
    const patientFilter = cfg.patientId ? { patient: `Patient/${cfg.patientId}` } : {};

    const [inWindowBundle, pastStartBundle] = await Promise.all([
      cfg.medplum.searchResources('Appointment', {
        ...patientFilter,
        date: `ge${lookback}`,
        status: PORTAL_IN_WINDOW_APPOINTMENT_STATUSES,
        _sort: 'date',
        _count: '100',
      }),
      cfg.medplum.searchResources('Appointment', {
        ...patientFilter,
        'date:missing': 'false',
        date: `lt${now}`,
        _sort: '-date',
        _count: '50',
      }),
    ]);

    const merged = new Map<string, Appointment>();
    for (const apt of [...inWindowBundle, ...pastStartBundle]) {
      if (apt.id) merged.set(apt.id, apt);
    }

    const upcomingNorm: PortalAppointment[] = [];
    const pastNorm: PortalAppointment[] = [];
    const practitionerLabels = await loadPractitionerPreferredLabelMap(
      cfg.medplum,
      [...merged.values()].flatMap((apt) => {
        const id = practitionerIdFromRef(
          apt.participant?.find((p) => p.actor?.reference?.startsWith('Practitioner/'))?.actor?.reference,
        );
        return id ? [id] : [];
      }),
    );
    for (const apt of merged.values()) {
      const normalized = normalizeAppointment(apt, cfg.patientId, practitionerLabels, nowMs);
      if (isPortalPastAppointment(apt, normalized, nowMs, cfg.patientId)) {
        pastNorm.push(normalized);
      } else {
        upcomingNorm.push(normalized);
      }
    }

    upcomingNorm.sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
    );
    pastNorm.sort(
      (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime(),
    );
    const [upcoming, past] = await Promise.all([
      enrichAppointmentsWithEpisodeOfCare(cfg.medplum, cfg.patientId, upcomingNorm),
      enrichAppointmentsWithEpisodeOfCare(cfg.medplum, cfg.patientId, pastNorm),
    ]);

    return {
      upcoming,
      past,
      source: 'medplum',
    };
  } catch (err) {
    console.error('[appointments] Live fetch failed:', err);
    return {
      upcoming: [],
      past: [],
      source: 'medplum',
    };
  }
}
